import React, { useCallback, useMemo, useRef } from 'react';
import { shallowEqual, useDispatch, useSelector, useStore } from 'react-redux';
import { site } from '@yola/ws-sdk';
import bowser from 'yola-bowser';
import config from 'src/js/modules/config';
import mainMenu from 'src/js/modules/main-menu';
import useFeatureFlags from 'yola-editor/src/js/modules/feature-flags/hooks/use-feature-flags';
import AiChat from '../components/ai-chat';
import actions from '../actions';
import selectors from '../selectors';
import getChatOrigin from '../helpers/get-chat-origin';
import applyChatChanges from '../helpers/apply-chat-changes';
import saveSiteForChat from '../helpers/save-site-for-chat';
import useAiChatInteractions from '../hooks/use-ai-chat-interactions';
import useAiChatDismissal from '../hooks/use-ai-chat-dismissal';
import usePanelWidth from '../hooks/use-panel-width';
import { DISPLAY_MODES, AI_CHAT_FEATURE_FLAG } from '../constants/common';

const isMobile = bowser.mobile || bowser.tablet;

const AiChatContainer = () => {
  const dispatch = useDispatch();
  const store = useStore();
  const url = useSelector(config.selectors.getAiChatURL);
  const siteId = useSelector(site.selectors.getSiteId);
  const isOpen = useSelector(selectors.getIsOpen);
  const displayMode = useSelector(selectors.getDisplayMode);
  const panelWidth = useSelector(selectors.getPanelWidth);
  const hostState = useSelector(selectors.getHostState, shallowEqual);
  const isMainMenuOpen = useSelector(mainMenu.selectors.getVisibilityStatus);
  const [featureFlags] = useFeatureFlags([AI_CHAT_FEATURE_FLAG]);
  const isFeatureEnabled = featureFlags[AI_CHAT_FEATURE_FLAG];

  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const iframeRef = useRef(null);
  const dismissRefs = useMemo(() => [triggerRef, panelRef], []);

  const chatOrigin = useMemo(() => getChatOrigin(url), [url]);

  const setOpen = useCallback((value) => dispatch(actions.setOpen(value)), [dispatch]);
  const setDisplayMode = useCallback(
    (value) => dispatch(actions.setDisplayMode(value)),
    [dispatch]
  );
  const getHostState = useCallback(() => selectors.getHostState(store.getState()), [store]);
  const handleTriggerClick = useCallback(() => setOpen(!isOpen), [setOpen, isOpen]);
  const handleDismiss = useCallback(() => setOpen(false), [setOpen]);

  const handleReload = useCallback(async (payload) => {
    try {
      await applyChatChanges(payload);
    } catch (e) {
      console.error('Error reloading site meta requested by AI chat', e);
    }
  }, []);

  usePanelWidth(panelWidth);

  useAiChatInteractions({
    iframeRef,
    chatOrigin,
    hostState,
    getHostState,
    onSetOpen: setOpen,
    onSetDisplayMode: setDisplayMode,
    onSave: saveSiteForChat,
    onSiteModificationEnd: handleReload,
    onReload: handleReload,
  });

  // The sidebar is docked, so it stays put until the chat app closes it.
  useAiChatDismissal({
    enabled: isOpen && displayMode === DISPLAY_MODES.FLOATING,
    refs: dismissRefs,
    onDismiss: handleDismiss,
  });

  if (isMobile || !isFeatureEnabled || !siteId) return null;

  return (
    <AiChat
      url={url}
      siteId={siteId}
      isOpen={isOpen}
      displayMode={displayMode}
      isMainMenuOpen={isMainMenuOpen}
      onTriggerClick={handleTriggerClick}
      triggerRef={triggerRef}
      panelRef={panelRef}
      iframeRef={iframeRef}
    />
  );
};

export default AiChatContainer;
